import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Lock, Star } from "lucide-react";
import { getAllSeries, getChaptersForSeries } from "../data/series";
import { useAuth } from "../AuthContext";

const LibraryPage = () => {
  const series = getAllSeries();
  const { isPatron } = useAuth();
  const [activeTab, setActiveTab] = useState(series[0]?.slug || "the-lattice");

  const activeSeries = series.find(s => s.slug === activeTab);
  const chapters = getChaptersForSeries(activeTab);
  const isGated = activeSeries?.tier === "patron" && !isPatron;

  return (
    <div data-testid="library-page" className="min-h-screen bg-ff-bg pt-24 md:pt-32">
      {/* Header */}
      <div className="max-w-5xl mx-auto px-4 md:px-6 mb-12">
        <h1 className="font-serif text-display text-ff-text mb-4">
          The Library
        </h1>
        <p className="text-ff-text-muted font-light text-lg max-w-2xl">
          Every chapter, in order. Three series free to read. Patron exclusives marked with a star.
        </p>
      </div>

      {/* Tabs */}
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <div 
          data-testid="series-tabs"
          className="flex border-b border-ff-border overflow-x-auto"
        >
          {series.map((s) => (
            <button
              key={s.slug}
              data-testid={`tab-${s.slug}`}
              onClick={() => setActiveTab(s.slug)}
              className={`tab-button whitespace-nowrap flex items-center gap-1.5 ${
                activeTab === s.slug ? "active" : ""
              }`}
              style={{
                color: activeTab === s.slug ? s.accentColor : undefined,
                "--tab-color": s.accentColor
              }}
            >
              {s.tier === "patron" && <Star size={12} fill="currentColor" />}
              {s.title}
            </button>
          ))}
        </div>
      </div>

      {/* Chapter List */}
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
        {activeSeries && (
          <div data-testid="chapter-list">
            {/* Series Info */}
            <div className="mb-8 pb-8 border-b border-ff-border">
              <div className="flex items-center gap-3 mb-2">
                <p className="label" style={{ color: activeSeries.accentColor }}>{activeSeries.tag}</p>
                {isGated && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full" style={{ background: '#c8860a22', color: '#c8860a' }}>
                    <Star size={10} fill="currentColor" /> Patron
                  </span>
                )}
              </div>
              <p className="text-ff-text-muted font-light text-sm">{activeSeries.description}</p>
            </div>

            {/* Patron gate notice */}
            {isGated && (
              <div className="mb-6 p-5 border border-ff-border text-center" style={{ borderColor: '#c8860a33', background: '#c8860a08' }}>
                <p className="text-ff-text-muted text-sm mb-3">
                  <strong className="text-ff-text">{activeSeries.title}</strong> is a Patron exclusive. Become a Patron to unlock all chapters.
                </p>
                <Link to="/subscribe" className="btn-primary inline-flex items-center gap-2 text-sm" style={{ backgroundColor: '#c8860a' }}>
                  <Star size={14} /> Become a Patron — $9.99/mo
                </Link>
              </div>
            )}

            {/* Chapters */}
            <div className="space-y-0">
              {chapters.map((chapter) => {
                const Wrapper = isGated ? 'div' : Link;
                const wrapperProps = isGated
                  ? { className: "chapter-item flex items-center justify-between p-4 border-b border-ff-border opacity-60" }
                  : { to: `/${activeTab}/${chapter.slug}`, className: "chapter-item flex items-center justify-between p-4 border-b border-ff-border group" };
                return (
                  <Wrapper key={chapter.slug} data-testid={`chapter-item-${chapter.number}`} {...wrapperProps}>
                    <div className="flex items-center gap-4">
                      <span className="w-8 h-8 flex items-center justify-center text-xs font-semibold rounded-full" style={{ backgroundColor: `${activeSeries.accentColor}20`, color: activeSeries.accentColor }}>
                        {isGated ? <Lock size={12} /> : chapter.number}
                      </span>
                      <div>
                        <h3 className="font-serif text-ff-text group-hover:text-opacity-80 transition-colors">{chapter.title}</h3>
                        <p className="text-ff-text-muted text-xs mt-1">Chapter {chapter.number}</p>
                      </div>
                    </div>
                    {isGated ? (
                      <span className="text-xs text-ff-text-muted">Locked</span>
                    ) : (
                      <ChevronRight size={20} className="text-ff-text-muted group-hover:text-ff-text group-hover:translate-x-1 transition-all" />
                    )}
                  </Wrapper>
                );
              })}
            </div>

            {/* Series CTA */}
            <div className="mt-12 text-center">
              <Link
                to={`/${activeTab}`}
                data-testid="view-series-btn"
                className="btn-secondary inline-flex items-center gap-2"
              >
                View {activeSeries.title} Series Page
                <ChevronRight size={14} />
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LibraryPage;
